import React from "react";
import { motion } from "framer-motion";
import {
  FaEnvelope,
  FaPhone,
  FaLocationDot,
  FaWhatsapp,
  FaLinkedin,
  FaGithub,
} from "react-icons/fa6";
import cvData from "../data/cvData";
import { fadeUp, staggerContainer, staggerItem, viewportOnce, easing } from "../utils/motionVariants";
import "./Contact.css";
import "./Section.css";

export default function Contact() {
  const { contacts, location, name } = cvData;
  const whatsappNumber = (contacts.phone || "").replace(/\D/g, "");
  const whatsappText = encodeURIComponent(
    "Hi Hirun, I came from your portfolio website and would like to chat."
  );
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappText}`;

  const methods = [
    {
      label: "Call",
      href: `tel:${contacts.phone.replace(/\s/g, "")}`,
      icon: <FaPhone />,
      detail: "Schedule a quick chat",
    },
    {
      label: "WhatsApp",
      href: whatsappUrl,
      icon: <FaWhatsapp />,
      detail: "Fastest way to reach me",
    },
    {
      label: "LinkedIn",
      href: contacts.linkedin,
      icon: <FaLinkedin />,
      detail: "Professional updates",
    },
    {
      label: "Email",
      href: `mailto:${contacts.email}`,
      icon: <FaEnvelope />,
      detail: "Detailed project briefs",
    },
  ];

  const faqs = [
    {
      q: "What type of roles are you looking for?",
      a: "I'm keen on full-stack and product engineering challenges where I can blend UI craft, backend architecture, and AI-driven tooling.",
    },
    {
      q: "Are you open to remote or hybrid?",
      a: "Absolutely. I collaborate across timezones and can join hybrid or onsite workshops when needed.",
    },
    {
      q: "What's your response time?",
      a: "I typically respond within 24 hours. WhatsApp or email are the fastest ways to reach me.",
    },
  ];

  const availabilityTags = [
    "Full-stack Development",
    "Remote Work",
    "Consulting",
    "Team Collaboration",
  ];

  const socialLinks = [
    {
      href: contacts.github || "https://github.com/hirun-kolongahapitiya",
      label: "GitHub",
      icon: <FaGithub />,
    },
    {
      href: contacts.linkedin || "https://www.linkedin.com/in/hirun-kolongahapitiya/",
      label: "LinkedIn",
      icon: <FaLinkedin />,
    },
    {
      href: `mailto:${contacts.email}`,
      label: "Email",
      icon: <FaEnvelope />,
    },
  ];

  return (
    <section id="contact" className="section section-mint contact-section">
      <motion.div
        className="contact-header"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.div
          className="contact-avatar"
          variants={staggerItem}
          whileHover={{ scale: 1.06, rotate: -3 }}
          transition={{ duration: 0.3, ease: easing }}
        >
          <img
            src="/assets/get-in-touch.jpeg"
            alt={`${name || "Hirun Kolongahapitiya"} portrait`}
            loading="lazy"
          />
        </motion.div>
        <motion.h2 variants={staggerItem}>Get In Touch</motion.h2>
        <motion.p variants={staggerItem}>
          I'm actively partnering with teams to build delightful digital products. If you have a role, collaboration,
          or idea to explore, let's start a conversation.
        </motion.p>
        <motion.div className="contact-social" variants={staggerItem}>
          <p className="eyebrow">Social</p>
          <div className="contact-social-links">
            {socialLinks.map((link) => (
              <motion.a
                key={link.label}
                href={link.href}
                aria-label={link.label}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.18, y: -3, rotate: -6 }}
                whileTap={{ scale: 0.92 }}
                transition={{ duration: 0.2, ease: easing }}
              >
                {link.icon}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </motion.div>

      <div className="contact-layout">
        <div className="contact-main">
          <motion.div
            className="panel"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeUp}
          >
            <div className="panel-head">
              <div>
                <p className="eyebrow">Contact Me</p>
                <h3>Choose your preferred channel</h3>
              </div>
            </div>
            <motion.div
              className="contact-methods"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              {methods.map((method) => (
                <motion.a
                  key={method.label}
                  href={method.href}
                  className="method-card"
                  target={method.label === "WhatsApp" || method.label === "LinkedIn" ? "_blank" : undefined}
                  rel="noreferrer"
                  variants={staggerItem}
                  whileHover={{ y: -6, scale: 1.03, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.25, ease: easing }}
                >
                  <motion.div
                    className="method-icon"
                    whileHover={{ rotate: 12, scale: 1.15 }}
                    transition={{ duration: 0.3 }}
                  >
                    {method.icon}
                  </motion.div>
                  <div>
                    <strong>{method.label}</strong>
                    <p>{method.detail}</p>
                  </div>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="panel faq-panel"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeUp}
          >
            <div className="panel-head">
              <div>
                <p className="eyebrow">Frequently Asked Questions</p>
                <h3>Answers upfront</h3>
              </div>
            </div>
            <motion.div
              className="faq-list"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              {faqs.map((faq, index) => (
                <motion.div
                  className="faq-item"
                  key={faq.q}
                  variants={staggerItem}
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="faq-index">{index + 1}</div>
                  <div>
                    <strong>{faq.q}</strong>
                    <p>{faq.a}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <aside className="contact-sidebar">
          <motion.div
            className="panel info-panel"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeUp}
            whileHover={{ y: -4, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)" }}
            transition={{ duration: 0.3, ease: easing }}
          >
            <div className="panel-head">
              <p className="eyebrow">Contact Information</p>
            </div>
            <ul>
              <li>
                <FaEnvelope /> <a href={`mailto:${contacts.email}`}>{contacts.email}</a>
              </li>
              <li>
                <FaPhone /> <a href={`tel:${contacts.phone.replace(/\s/g, "")}`}>{contacts.phone}</a>
              </li>
              <li>
                <FaLocationDot /> {location}
              </li>
              <li>
                <FaGithub />{" "}
                <a href={contacts.github || "https://github.com/hirun-kolongahapitiya"} target="_blank" rel="noreferrer">
                  GitHub Profile
                </a>
              </li>
              <li>
                <FaLinkedin />{" "}
                <a href={contacts.linkedin} target="_blank" rel="noreferrer">
                  Connect on LinkedIn
                </a>
              </li>
            </ul>
          </motion.div>

          <motion.div
            className="panel availability-panel"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeUp}
            whileHover={{ y: -4, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)" }}
            transition={{ duration: 0.3, ease: easing }}
          >
            <div className="panel-head">
              <p className="eyebrow">Availability</p>
              <h3>Open to new opportunities</h3>
            </div>
            <p>
              I'm currently exploring short-term and long-term collaborations. Let's discuss how I can contribute to
              your next launch.
            </p>
            <motion.div
              className="tag-cloud"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              {availabilityTags.map((tag) => (
                <motion.span
                  key={tag}
                  variants={staggerItem}
                  whileHover={{ scale: 1.08, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  {tag}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        </aside>
      </div>
    </section>
  );
}
